import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Table } from "@4uhub/lib4uhub";

import MainApiService from "../../../../services/mainApi.service";
import { IHealthPrograms } from "../../../../models/health-programs";
import { useMemo } from "react";
import { IContents } from "../../../../models/content";
import { Chip } from "@mui/material";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { deleteHealthProgramRuleContent } from "../../../../services/content.service";

const HEALTH_PROGRAMS_ROUTE =
  process.env.REACT_APP_MAIN +
  "/api/v1/HealthProgramContentRule/GetAllContentWithPagination/";
const healthProgramsService = new MainApiService<IContents>(
  HEALTH_PROGRAMS_ROUTE
);

const translationPath = "page.4uHcp.hcp.rulesContents.";

const RulesContentsPage = () => {
  const navigate = useNavigate();

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { ruleId } = useParams();

  const columns: GridColDef<IContents>[] = [
    {
      field: "title",
      headerName: t(translationPath + "title") || "Title",
      renderCell: (params) => {
        const title = params.row.contents.find(
          (content) => content.language.code === language
        )?.title;
        return title ? title : "-";
      },
    },
    {
      field: "publicationDate",
      headerName: t(translationPath + "publication_date") || "Publication date",
      renderCell: (params) => {
        const date = new Date(params.value);
        return format(date, "Pp", {
          locale: language === "pt-BR" ? ptBR : enUS,
        }).replaceAll(",", " -");
      },
    },
    {
      field: "contentStatus",
      headerName: t(translationPath + "content_status") || "Content status",
      maxWidth: 150,
      renderCell: (params) => {
        return (
          <Chip
            label={
              params.row.contentStatus.code === "2"
                ? t(translationPath + "published")
                : t(translationPath + "not_published")
            }
            color={params.row.contentStatus.code === "2" ? "success" : "error"}
            size="small"
          />
        );
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 150,
      renderCell: (params) => {
        return (
          <Chip
            label={
              params.row.enable
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={params.row.enable ? "success" : "error"}
            size="small"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IHealthPrograms>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const props = useMemo(() => {
    return {
      ContentTypeCode: "2",
      HealthProgramContentRuleId: ruleId,
    };
  }, [ruleId]);

  return (
    <Table<IContents>
      showDefaultMacroFilters={false}
      service={healthProgramsService}
      columns={columns}
      title={t(translationPath + "rules_contents")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      backButton
      serviceProps={props}
      handleDeleteItem={(id) =>
        deleteHealthProgramRuleContent(ruleId ?? "", id)
      }
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a rule content"
      }
      addButtonLabel={
        t(translationPath + "add_rule_content") || "Add Rule Content"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default RulesContentsPage;
